import { Grid, Stack } from '@mui/material'
import { DynamicFlowStep } from 'components/common/DynamicFlow'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { GalleryImage } from 'components/common/Gallery/GalleryImage'
import { MUITooltip } from 'components/common/MUITooltip'

import { APIRequestErrorType } from 'constants/API'
import { Color } from 'constants/assets'
import { FileType } from 'constants/misc'
import { CONST_PRODUCT_SERIAL } from 'constants/product'
import { GRAY_100 } from 'constants/styling/theme'
import { VisualFileType } from 'constants/visual'
import { Nullable } from 'models/helpers'
import { UploadImage } from 'models/redux'
import { FC, Fragment, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ReactLoading from 'react-loading'
import { usePurchaseSessionVisualOperations } from 'utils/hooks/usePurchaseSessionVisualOperations'
import { ErrorMessageType, errorMessageType } from 'utils/helpers'
import { useImageRegex } from 'utils/hooks'
import { logger } from 'utils/logging'
import { useClientProperty } from '../_main/contexts'
import { useClientPropertySelfUploadPopup } from './ClientPropertySelfUploadPopup.context'
import { UploadVisualsStepKey } from './ClientPropertySelfUploadPopup.controller'

export const UploadVisualsStep: FC = () => {
  const { t } = useTranslation(['gallery', 'deal_assignment', 'common'])
  const { uploadVisual: uploadPSVisual, deleteVisual: deletePSVisual } = usePurchaseSessionVisualOperations()

  const { sessionId } = useClientProperty()
  const {
    productId,
    listedVisualKeys,
    visualsCount,
    downloadVisualsEntries,
    uploadVisualsEntries,
    isUploadRunning,
    sortEntriesFunction
  } = useClientPropertySelfUploadPopup()

  const imageNameReplacePattern = useImageRegex(sessionId ?? '')

  const onDeleteImage = useCallback((reactKey: string) => {
    if (!sessionId || !productId) return

    deletePSVisual({ sessionId, productId, productSerial: CONST_PRODUCT_SERIAL, filename: reactKey })
  }, [deletePSVisual, productId, sessionId])

  const preloadListing = useCallback(() => {
    const tupleArray: [string, string, JSX.Element][] = listedVisualKeys.map(key => {
      const reactKey = key
      return [key, reactKey, (
        <GalleryImage key={reactKey}>
          <div className="text">
            <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
          </div>
        </GalleryImage>
      )]
    })

    return tupleArray
  }, [listedVisualKeys])


  const downloadListing = useCallback(() => {
    const tupleArray: [string, string, JSX.Element][] = downloadVisualsEntries.map(([key, allTypes]) => {
      const img = allTypes?.[VisualFileType.RAW_THUMB]
      if (!img) throw new Error(`Image does not exist for key: ${key} and visual type: ${VisualFileType.RAW_THUMB}`)
      const isError = img.request.error_type !== APIRequestErrorType.NONE
      const errorMsg = img.request.error
      const reactKey = key

      return [key, reactKey, (
        <GalleryImage
          key={reactKey}
          imageUrl={img.signedUrl}
          isDeletable
          isError={isError}
          errorMessage={errorMsg}
          onDelete={() => onDeleteImage(reactKey)}
        >
          {img.deleting &&
            <Stack
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="100%"
              sx={{ opacity: 0.8, background: GRAY_100 }}
            >
              <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
              <span>{t('deleting')}</span>
            </Stack>
          }
        </GalleryImage>
      )]
    })

    return tupleArray
  }, [downloadVisualsEntries, onDeleteImage, t])

  const uploadImageContent = useCallback((img: Nullable<UploadImage>) => {
    if (!img) return
    const isError = img.request.error_type !== APIRequestErrorType.NONE
    const errorMsg = img.request.error
    const isOtherMsg = errorMessageType(errorMsg) === ErrorMessageType.OTHER

    if (img.deleting) {
      return (
        <Fragment>
          <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
          <span>{t('deleting')}</span>
        </Fragment>
      )
    }

    if (img.progress === 100) {
      if (isError) {
        return (
          <span>
            <Trans t={t} i18nKey="upload_failed_description">
              <MUITooltip placement="top" content={isOtherMsg ? errorMsg : t(`image_processing_error.${errorMsg}`)}>
                <span style={{ cursor: 'pointer' }} data-tip data-for="re-upload-tooltip">&nbsp;</span>
              </MUITooltip>
            </Trans>
          </span>
        )
      }

      return (
        <Stack
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
          sx={{ opacity: 0.8, background: GRAY_100 }}
        >
          <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
          <span>{t('processing')}</span>
        </Stack>
      )
    }

    return (
      <span>
        {t('uploading')}: {img.progress || 0}%
      </span>
    )
  }, [t])

  const uploadListing = useCallback(() => {
    const tupleArray: [string, string, string, JSX.Element][] = uploadVisualsEntries.map(([key, allTypes]) => {
      const img = allTypes?.[VisualFileType.RAW]
      if (!img) throw new Error(`Image does not exist for key: ${key} and visual type: ${VisualFileType.RAW}`)
      // For sorting fresh uploads on top
      const sortKey = (img.replaces || imageNameReplacePattern.exec(key)) ? key : `${sessionId}-bkbn-ZZZZZ-${img.droppedIn}-${key}`
      const reactKey = img.originalFilename ?? key

      return [key, reactKey, sortKey, (
        <GalleryImage
          key={reactKey}
          imageUrl={img.signedUrl}
          isDeletable
          onDelete={() => onDeleteImage(key)}
        >
          {uploadImageContent(img)}
        </GalleryImage>
      )]
    })

    return tupleArray
  }, [uploadVisualsEntries, imageNameReplacePattern, sessionId, uploadImageContent, onDeleteImage])

  const visualsListing = useCallback(() => {
    const map: Map<string, [string, JSX.Element]> = new Map()

    for (let [key, reactKey, node] of preloadListing()) map.set(key, [reactKey, node])
    for (let [key, reactKey, node] of downloadListing()) map.set(key, [reactKey, node])
    for (let [key, reactKey, sortKey, node] of uploadListing()) {
      map.delete(key)
      map.set(sortKey, [reactKey, node])
    }

    const entries: [string, [string, JSX.Element]][] = Array.from(map.entries())
    const sortedEntries = [...entries].sort(sortEntriesFunction)

    return (
      <Fragment>
        {sortedEntries.map(([key, [reactKey, node]]) => (
          <Grid key={reactKey} item xs={3}>
            {node}
          </Grid>
        ))}
      </Fragment>
    )
  }, [sortEntriesFunction, preloadListing, downloadListing, uploadListing])

  return (
    <DynamicFlowStep
      stepKey={UploadVisualsStepKey.UPLOAD_PHOTOS}
      isValid={true}
    >
      <Stack alignItems="flex-start" gap={3} padding="0 0 1.6rem 0">

        <FileAPIUpload
          boxElevation='none'
          borderBoxColor='transparent'
          showFileListing={false}
          files={[]}
          disabled={isUploadRunning}
          acceptedFileTypes={[FileType.JPEG]}
          uploadHandler={(acceptedFiles) => {
            if (!sessionId || !productId) {
              logger.warn('Self-upload drop ignored: sessionId or productId missing', null, {
                context: {
                  component: 'UploadVisualsStep',
                  action: 'drop',
                  metadata: { hasSessionId: !!sessionId, hasProductId: !!productId, fileCount: acceptedFiles.length },
                },
              })
              return
            }

            for (let file of acceptedFiles) {
              uploadPSVisual({ sessionId, productId, productSerial: CONST_PRODUCT_SERIAL, file, email: '' })
            }
          }}
        />

        {visualsCount > 0 &&
          <Grid container spacing={1} padding="0 2rem">
            {visualsListing()}
          </Grid>
        }

      </Stack>
    </DynamicFlowStep>
  )
}
